import { useMemo, useState } from 'react';
import * as Sentry from '@sentry/nextjs';
import { StrategyBuilder } from 'utils/shared/quantlib/StrategyBuilder';
import { CALL, PUT, LONG, SHORT, dateStringToMarketClose } from 'utils/shared/quantlib/utils';
import {
  dateToDateString,
  calculateTimeToExpirationInYears,
} from 'utils/shared/quantlib/BlackScholes';
import {
  useStrategyCalculationsCache,
  type StrategyCalculationData,
} from 'screens/OptionsProfitCalculator/strategyCalculationsCache';
import { CallPut } from 'utils/shared/trades/types';
import { normalizeCallPut } from 'utils/shared/trades/normalizeCallPut';
import {
  getOptionFlowStrategyPosition,
  OptionFlowStrategyPosition,
} from 'utils/shared/trades/getOptionFlowRowSentiment';
import { useRiskFreeRates } from './useRiskFreeRates';

export const usePayoffCalculation = (trade: any, isOpen: boolean = false) => {
  const [calculationError, setCalculationError] = useState<Error | null>(null);
  const strategyCalculationsCache = useStrategyCalculationsCache();
  const { rates: riskFreeRates, loading: riskFreeRatesLoading } = useRiskFreeRates(isOpen);

  const ticker = trade?.ticker;
  const premiumPerContract = trade?.price;
  const spot = trade?.spot || trade?.priceExternalApi || 0;
  const strike = trade?.strike || 0;
  const premium = trade?.premium || 0;
  const size = trade?.size || 1;
  const cp = normalizeCallPut(trade?.cp) || CallPut.Call;
  const isCall = cp === CallPut.Call;
  const strategyPosition = useMemo(
    () => getOptionFlowStrategyPosition(trade),
    [trade?.tradeDirection, trade?.tradeSide],
  );

  const expirationDate = useMemo(() => {
    if (!trade?.expireYear || !trade?.expireMonth || !trade?.expireDay) {
      const futureDate = new Date();
      futureDate.setDate(futureDate.getDate() + 30);
      return futureDate;
    }

    const calculatedDate = dateStringToMarketClose(
      `${trade.expireYear}-${String(trade.expireMonth).padStart(2, '0')}-${String(
        trade.expireDay,
      ).padStart(2, '0')}`,
    );

    const now = new Date();
    if (calculatedDate <= now) {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      return tomorrow;
    }

    return calculatedDate;
  }, [trade]);

  // Select appropriate risk-free rate based on expiration
  const getRiskFreeRate = useMemo(() => {
    if (!riskFreeRates) {
      return 0.05; // Default fallback
    }

    const now = new Date();
    const timeToExpirationMs = expirationDate.getTime() - now.getTime();
    const timeToExpirationMonths = timeToExpirationMs / (1000 * 60 * 60 * 24 * 30);

    if (timeToExpirationMonths < 1) {
      return riskFreeRates['1m'];
    } else if (timeToExpirationMonths < 2) {
      return riskFreeRates['2m'];
    } else if (timeToExpirationMonths < 3) {
      return riskFreeRates['3m'];
    } else if (timeToExpirationMonths < 6) {
      return riskFreeRates['6m'];
    } else if (timeToExpirationMonths < 12) {
      return riskFreeRates['1y'];
    } else {
      return riskFreeRates['2y'];
    }
  }, [riskFreeRates, expirationDate]);

  const expirationKey = useMemo(() => {
    if (!trade?.expireYear || !trade?.expireMonth || !trade?.expireDay) return '';
    return `${trade.expireYear}-${String(trade.expireMonth).padStart(2, '0')}-${String(
      trade.expireDay,
    ).padStart(2, '0')}`;
  }, [trade]);

  const tradeCacheKey = useMemo(() => {
    return `single-${cp}-${strategyPosition}-${strike}-${expirationKey}`;
  }, [cp, strategyPosition, strike, expirationKey]);

  const calculationData = useMemo<StrategyCalculationData | null>(() => {
    if (
      !spot ||
      !strike ||
      !premium ||
      !riskFreeRates ||
      strategyPosition === OptionFlowStrategyPosition.Neutral
    ) {
      return null;
    }

    const cached = strategyCalculationsCache.getCacheValue(tradeCacheKey, ticker, expirationKey);
    if (cached) {
      return cached;
    }

    try {
      const optionType = isCall ? CALL : PUT;
      const isLongPosition = strategyPosition === OptionFlowStrategyPosition.Long;
      const position = isLongPosition ? LONG : SHORT;
      const numberOfContracts = isLongPosition ? size : -size;

      const impliedVol = trade?.impliedVol || 0.3;
      const riskFreeRate = getRiskFreeRate;

      const builder = new StrategyBuilder({
        strategyOptionParams: [
          {
            type: optionType,
            spot,
            strike,
            premium: premiumPerContract,
            riskFreeRate,
            volatility: impliedVol,
            position,
            numberOfContracts: numberOfContracts,
            expirationDateString: dateToDateString(expirationDate),
          },
        ],
        priceRangePercentDecimal: 0.1,
        priceRangeMaxPercentDecimal: 0.5,
        currentSpot: spot,
        underlyingStockPositions: [],
      });

      const fullStrategy = builder.getFullStrategy(expirationDate);

      const timeToExpiration = calculateTimeToExpirationInYears(expirationDate);

      const optionDataParams = {
        spot,
        currentPrice: spot,
        strikePrice: strike,
        timeToExpiration,
        riskFreeRate: riskFreeRate,
        volatility: impliedVol,
        priceRangePercentDecimal: 0.2,
        data: fullStrategy.payoffGraph,
      };

      const probData = StrategyBuilder.generateProbabilityData(optionDataParams);

      const calcMaxProfit = fullStrategy.maxProfitLoss.maxProfit;
      const calcMaxLoss = fullStrategy.maxProfitLoss.maxLoss;
      const calcNormalizedProfitFactor = StrategyBuilder.calculateNormalizedProfitFactor({
        maxProfit: calcMaxProfit,
        maxLoss: calcMaxLoss,
        probabilityOfProfit: fullStrategy.probabilityOfProfit || 0,
        spot,
        volatility: impliedVol,
      });

      const strategyVals = {
        strategyMetrics: fullStrategy.strategyMetrics,
        maxProfitLoss: fullStrategy.maxProfitLoss,
        probabilityOfProfit: fullStrategy.probabilityOfProfit || 0,
        hasErraticBehavior: false,
      };

      const result: StrategyCalculationData = {
        payoffGraph: fullStrategy.payoffGraph,
        breakEvenPoints: fullStrategy.breakEvenPoints,
        probabilityData: probData,
        maxProfitLoss: fullStrategy.maxProfitLoss,
        strategyValues: strategyVals,
        normalizedProfitFactor: calcNormalizedProfitFactor,
        spot,
        loading: false,
        payoffTable: fullStrategy.payoffTable,
      };

      strategyCalculationsCache.setCacheValue(tradeCacheKey, ticker, expirationKey, result);

      setCalculationError(null); // Clear any previous errors
      return result;
    } catch (error) {
      const errorObj = error instanceof Error ? error : new Error(String(error));
      console.error('usePayoffCalculation - Error calculating payoff:', errorObj);

      // Report to Sentry for monitoring
      Sentry.withScope((scope) => {
        scope.setContext('trade', {
          ticker,
          strike,
          spot,
          premium,
          expirationDate: dateToDateString(expirationDate),
        });
        scope.setTag('error_location', 'usePayoffCalculation');
        Sentry.captureException(errorObj);
      });

      setCalculationError(errorObj);
      return null;
    }
  }, [
    spot,
    strike,
    premium,
    size,
    isCall,
    expirationDate,
    expirationKey,
    trade?.tradeDirection,
    trade?.tradeSide,
    trade?.impliedVol,
    trade,
    strategyPosition,
    strategyCalculationsCache,
    tradeCacheKey,
    ticker,
    cp,
    riskFreeRates,
    getRiskFreeRate,
  ]);

  const impliedVol = trade?.impliedVol || 0.3;

  return {
    calculationData,
    ticker,
    spot,
    strike,
    premium,
    volatility: impliedVol,
    loading: riskFreeRatesLoading,
    error: calculationError,
  };
};
